import { getId, isConstructedOngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import type { OngoingDownload } from '@mimir/client-common/model/ongoingDownload';
import { showToast, TOAST, DONT_AUTO_HIDE_TOAST } from '../eventBus';
import { refreshPremiereClipInfos } from '../premiere/syncing';
import { isInPremiere, type ApplicationContext } from '@mimir/common/context/applicationContext';
import type { NotificationOps } from '@mimir/client-common/fileSync/notificationOps';
import type { Logger } from '@mimir/common/types/logger';
import type { OngoingDownloadOps } from '@mimir/client-common/fileSync/ongoingDownloadOps';
import { invariant } from '@mimir/common/util/invariant';
import type { LocalStateOps } from '@mimir/client-common/fileSync/localStateOps';


async function cancelOngoingDownloadInternal(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  transferRequest: OngoingDownload,
  skipToast: boolean,
  skippedFileExtensions: string[],
) {
  if (isConstructedOngoingDownload(transferRequest)) {
    transferRequest.cancelTokenSource.cancel();
  }
  services.ongoingDownloadOps.deleteFromOngoingDownloads(getId(transferRequest));
  let description = '';
  if (transferRequest.artefact === 'proxy') {
    description = ' (proxy)';
  } else if (transferRequest.artefact === 'aaf') {
    description = ' (AAF file)';
  }

  if (!skipToast) {
    if (transferRequest.error) {
      showToast(`Error downloading ${transferRequest.originalFileName}${description}: ${transferRequest.error}`, TOAST.ERROR, DONT_AUTO_HIDE_TOAST);
    } else {
      showToast(`Cancelled download of ${transferRequest.originalFileName}${description}`, TOAST.INFO);
    }
  }

  // refresh premiere sync page to reflect new state
  const ctx = services.applicationContext;
  if (isInPremiere(ctx)) {
    invariant(services.localStateOps, 'localStateOps should be defined in Premiere context');
    await refreshPremiereClipInfos({
      applicationContext: ctx,
      notificationOps: services.notificationOps,
      logger: services.logger,
      ongoingDownloadOps: services.ongoingDownloadOps,
      localStateOps: services.localStateOps,
    }, skippedFileExtensions);
  }
}

export async function cancelOngoingDownloadWithoutToast(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  ongoingDownload: OngoingDownload,
  skippedFileExtensions: string[],
) {
  await cancelOngoingDownloadInternal(services, ongoingDownload, true, skippedFileExtensions);
}

export async function cancelOngoingDownload(
  services: {
    applicationContext: ApplicationContext,
    notificationOps: NotificationOps,
    logger: Logger,
    ongoingDownloadOps: OngoingDownloadOps,
    localStateOps: LocalStateOps | undefined,
  },
  ongoingDownload: OngoingDownload,
  skippedFileExtensions: string[],
) {
  await cancelOngoingDownloadInternal(services, ongoingDownload, false, skippedFileExtensions);
}
